import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

const useLayoutState = () => useSelector(({ layout }) => layout);
const useAuthState = () => useSelector(({ auth }) => auth);
const useThemesState = () => useSelector(({ themes }) => themes);

const useLayoutContext = () => {
  const { context } = useLayoutState();
  return { context };
};

const useRibbonMenu = () => {
  const { labels, fullscreen } = useLayoutState();
  return { hasLabels: labels || fullscreen, fullscreen };
};

const useRibbonToggle = () => {
  const { ribbonIsVisible: visible } = useLayoutState();
  return { visible };
};

const useTabsDropDown = () => {
  const { tabs } = useLayoutState();
  return { tabs };
};

const useWrappedTab = () => {
  const { currentUser, role: { permissions } } = useAuthState();
  return { currentUser, permissions };
};

const useRibbonActions = () => {
  const { currentUser, role: { approved, permissions } } = useAuthState();
  const { actions: { category, code, url }, fullscreen } = useLayoutState();
  return {
    approved, category, code, currentUser, fullscreen, permissions, url,
  };
};

const useRibbon = () => {
  const { fullscreen, labels: hasLabels, ribbonIsVisible: visible } = useLayoutState();
  const { theme } = useThemesState();
  return {
    fullscreen, hasLabels, visible, theme,
  };
};

const useLayoutLoader = () => {
  const { currentUser, role: { isLoading, approved }, commonsMode } = useAuthState();
  const { theme } = useThemesState();
  return {
    approved, commonsMode, currentUser, isLoading, theme,
  };
};

const makeSelectNavBar = () => createSelector(
  ({ layout }) => layout,
  ({ themes }) => themes.theme,
  ({ auth }) => auth.currentUser,
  (layout, theme, currentUser) => ({
    fullscreen: layout.fullscreen,
    hasRibbon: layout.hasRibbon,
    isGuest: currentUser ? currentUser.isGuest : true,
    theme,
  }),
);

const useNavBar = () => {
  const selector = useMemo(makeSelectNavBar, []);
  return useSelector(selector);
};

export default {
  useLayoutContext,
  useLayoutLoader,
  useNavBar,
  useRibbon,
  useRibbonActions,
  useRibbonMenu,
  useRibbonToggle,
  useTabsDropDown,
  useWrappedTab,
};
